import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuro } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Upload, FileDown } from "lucide-react";

// Upload of the SoftOne «Αγορές Αναλυτικά» (items) file, embedded in the Purchases area.
export const ItemsUploadCard = () => {
  const qc = useQueryClient();
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const { data: imports = [] } = useQuery({ queryKey: ["erp-imports"], queryFn: async () => (await api.get("/erp/imports")).data });
  const { data: templates = [] } = useQuery({ queryKey: ["erp-templates"], queryFn: async () => (await api.get("/erp/templates")).data });
  const tpl = templates.find((t) => t.kind === "items");
  const last = imports.find((i) => i.kind === "items" && i.action !== "delete");
  const up = useMutation({
    mutationFn: async (commit) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`/erp/import?commit=${commit}`, fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
    },
    onSuccess: (d) => {
      if (d.kind !== "items") { toast.error(`Το αρχείο αναγνωρίστηκε ως «${d.kind === "expenses" ? "Δαπάνες" : "Αγορές"}». Εδώ ανεβαίνει μόνο το «Αγορές Αναλυτικά» (ανά είδος).`); setPreview(null); return; }
      if (!d.committed) { setPreview(d); return; }
      toast.success(`Αγορές αναλυτικά: εισήχθησαν ${d.rows} γραμμές (${d.years.join(", ")})`);
      setPreview(null); setFile(null); if (ref.current) ref.current.value = "";
      ["erp-imports", "erp-templates", "erp-pivot", "erp-yoy", "erp-stats", "erp-prices", "erp-price-alerts"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
      if (d.new_price_alerts) toast.warning(`${d.new_price_alerts} νέες αυξήσεις τιμών πάνω από 10%`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const download = async () => {
    const res = await api.get("/erp/templates/items", { responseType: "blob" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(res.data); a.download = tpl.filename; a.click(); URL.revokeObjectURL(a.href);
  };
  return (
    <div className="rounded-2xl bg-white border border-[#E3DED4] p-4 space-y-3" data-testid="items-upload-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-medium text-[#45423C] flex items-center gap-2 text-sm"><Upload className="h-4 w-4 text-[#C86235]" /> Ανέβασμα «Αγορές Αναλυτικά» (SoftOne, ανά είδος/μήνα)</h2>
        {last && <span className="text-xs text-[#6F6A60]" data-testid="items-last-import">Τελευταίο: {new Date(last.at).toLocaleDateString("el-GR")} · {last.rows} γραμμές · {(last.years || []).join(", ")}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input data-testid="items-upload-file" ref={ref} type="file" accept=".xlsx" className="text-sm" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
        <button data-testid="items-upload-preview" disabled={!file || up.isPending} onClick={() => up.mutate(false)}
          className="rounded-xl border border-[#E3DED4] bg-white px-4 py-2 text-sm font-semibold text-[#45423C] hover:border-[#45423C] disabled:opacity-50">Προεπισκόπηση</button>
        {preview && (
          <button data-testid="items-upload-commit" disabled={up.isPending} onClick={() => up.mutate(true)}
            className="rounded-xl bg-[#45423C] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2F2D29] disabled:opacity-50">{up.isPending ? "Εισαγωγή…" : "Εισαγωγή"}</button>
        )}
        {tpl && <button data-testid="items-template-download" onClick={download} className="inline-flex items-center gap-1 text-xs text-[#C86235] hover:underline ml-auto"><FileDown className="h-3.5 w-3.5" /> Template</button>}
      </div>
      {preview && (
        <div className="rounded-xl border border-[#E3DED4] p-3 text-sm grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="items-upload-summary">
          <div><div className="text-[10px] uppercase text-[#6F6A60]">Γραμμές</div>{preview.rows}</div>
          <div><div className="text-[10px] uppercase text-[#6F6A60]">Καθαρή αξία</div>{fmtEuro(preview.net_total)}</div>
          <div><div className="text-[10px] uppercase text-[#6F6A60]">Πιστωτικά</div>{preview.credit_notes}</div>
          <div><div className="text-[10px] uppercase text-[#6F6A60]">Έτη (αντικαθίστανται)</div>{preview.years.join(", ")}</div>
        </div>
      )}
    </div>
  );
};
